import { useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ShieldCheck, Play, CheckCircle, XCircle, Clock, RefreshCw, Server, Database, Globe, Cpu } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'MongoDB Connection': Database,
  'Products Collection': Server,
  'Suppliers Collection': Server,
  'Invoices Collection': Server,
  'PCF History': Server,
  'Emission Trends': Server,
  'Unified Accounting': Server,
  'News Collection': Server,
  'Database Write/Delete': Database,
  'API Endpoints': Globe,
};

export default function SystemTest() {
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [frontendTests, setFrontendTests] = useState([]);

  const runTests = useCallback(async () => {
    setRunning(true);
    setResults(null);
    setFrontendTests([]);

    // Run backend tests
    let backendData = null;
    try {
      const res = await axios.get(`${API}/system-test`);
      backendData = res.data;
    } catch (e) {
      backendData = { total_tests: 1, passed: 0, failed: 1, score: 0, results: [{ test: 'Backend API', status: 'fail', message: e.message, duration_ms: 0 }] };
    }

    // Run frontend tests
    const feTests = [];
    // Test 1: React rendering
    feTests.push({ test: 'React App Rendering', status: document.getElementById('root') ? 'pass' : 'fail', message: document.getElementById('root') ? 'Root element found' : 'Root element missing', duration_ms: 1 });
    // Test 2: Theme system
    const hasTheme = document.documentElement.classList.contains('light') || document.documentElement.classList.contains('dark');
    feTests.push({ test: 'Theme System', status: hasTheme ? 'pass' : 'fail', message: hasTheme ? `Current: ${document.documentElement.classList.contains('dark') ? 'dark' : 'light'}` : 'No theme class found', duration_ms: 1 });
    // Test 3: Router
    feTests.push({ test: 'React Router', status: window.location.pathname ? 'pass' : 'fail', message: `Current path: ${window.location.pathname}`, duration_ms: 1 });
    // Test 4: LocalStorage
    try { localStorage.setItem('__test__', '1'); localStorage.removeItem('__test__'); feTests.push({ test: 'LocalStorage', status: 'pass', message: 'Read/write working', duration_ms: 1 }); }
    catch { feTests.push({ test: 'LocalStorage', status: 'fail', message: 'Not available', duration_ms: 0 }); }
    // Test 5: Environment
    feTests.push({ test: 'Backend URL Config', status: process.env.REACT_APP_BACKEND_URL ? 'pass' : 'fail', message: process.env.REACT_APP_BACKEND_URL ? 'Configured' : 'Missing REACT_APP_BACKEND_URL', duration_ms: 1 });

    // Test individual API endpoints
    const endpoints = [
      { name: 'Dashboard API', url: '/dashboard/stats' },
      { name: 'Products API', url: '/products' },
      { name: 'Suppliers API', url: '/suppliers' },
      { name: 'Invoices API', url: '/invoices' },
      { name: 'News API', url: '/news' },
    ];

    for (const ep of endpoints) {
      try {
        const start = performance.now();
        await axios.get(`${API}${ep.url}`);
        const dur = Math.round(performance.now() - start);
        feTests.push({ test: ep.name, status: 'pass', message: `Response in ${dur}ms`, duration_ms: dur });
      } catch (e) {
        feTests.push({ test: ep.name, status: 'fail', message: e.message, duration_ms: 0 });
      }
    }

    setFrontendTests(feTests);
    setResults(backendData);
    setRunning(false);
  }, []);

  const allTests = [...(results?.results || []), ...frontendTests];
  const totalPassed = allTests.filter(t => t.status === 'pass').length;
  const totalFailed = allTests.filter(t => t.status === 'fail').length;
  const totalAll = allTests.length;
  const score = totalAll > 0 ? Math.round((totalPassed / totalAll) * 100) : 0;

  return (
    <div data-testid="system-test-page" className="space-y-6">
      <div className="cp-page-header">
        <h1 className="cp-page-title flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-primary" /> System Test
        </h1>
        <Button onClick={runTests} disabled={running} className="bg-primary hover:bg-primary/90" data-testid="run-tests-btn">
          {running ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          {running ? 'Running Tests...' : 'Run All Tests'}
        </Button>
      </div>

      {!results && !running && (
        <Card className="p-12 text-center animate-fade-in" data-testid="test-placeholder">
          <Cpu className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">Click "Run All Tests" to check system health</p>
          <p className="text-xs text-muted-foreground/70 mt-1">Tests backend APIs, database connections, and frontend components</p>
        </Card>
      )}

      {running && (
        <Card className="p-12 text-center animate-fade-in" data-testid="test-running">
          <RefreshCw className="w-10 h-10 mx-auto text-primary animate-spin mb-4" />
          <p className="font-medium">Running system diagnostics...</p>
        </Card>
      )}

      {results && !running && (
        <div className="space-y-4 animate-fade-in">
          {/* Score Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card className={`p-5 text-center ${score === 100 ? 'border-green-500/30 bg-green-500/5' : score >= 80 ? 'border-amber-500/30 bg-amber-500/5' : 'border-red-500/30 bg-red-500/5'}`} data-testid="test-score">
              <p className="text-4xl font-heading font-bold" style={{ color: score === 100 ? '#166534' : score >= 80 ? '#D97706' : '#DC2626' }}>{score}%</p>
              <p className="text-xs text-muted-foreground mt-1">Overall Score</p>
            </Card>
            <Card className="p-5 text-center" data-testid="test-total">
              <p className="text-2xl font-heading font-bold">{totalAll}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Tests</p>
            </Card>
            <Card className="p-5 text-center" data-testid="test-passed">
              <p className="text-2xl font-heading font-bold text-green-600">{totalPassed}</p>
              <p className="text-xs text-muted-foreground mt-1">Passed</p>
            </Card>
            <Card className="p-5 text-center" data-testid="test-failed">
              <p className="text-2xl font-heading font-bold text-red-500">{totalFailed}</p>
              <p className="text-xs text-muted-foreground mt-1">Failed</p>
            </Card>
          </div>

          {/* Backend Results */}
          <div className="cp-chart-card" data-testid="backend-test-results">
            <h3 className="text-sm font-heading font-semibold mb-3 flex items-center gap-2">
              <Server className="w-4 h-4 text-muted-foreground" /> Backend Tests
            </h3>
            <div className="cp-table-wrapper">
              <Table>
                <TableHeader><TableRow><TableHead className="w-8"></TableHead><TableHead>Test</TableHead><TableHead>Status</TableHead><TableHead>Message</TableHead><TableHead className="text-right">Duration</TableHead></TableRow></TableHeader>
                <TableBody>
                  {results.results.map((r, i) => {
                    const Icon = iconMap[r.test] || Server;
                    return (
                      <TableRow key={i}>
                        <TableCell><Icon className="w-4 h-4 text-muted-foreground" /></TableCell>
                        <TableCell className="font-medium text-sm">{r.test}</TableCell>
                        <TableCell>
                          {r.status === 'pass'
                            ? <span className="cp-badge-green flex items-center gap-1 w-fit"><CheckCircle className="w-3 h-3" />Pass</span>
                            : <span className="cp-badge-red flex items-center gap-1 w-fit"><XCircle className="w-3 h-3" />Fail</span>}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.message}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.duration_ms}ms</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Frontend Results */}
          <div className="cp-chart-card" data-testid="frontend-test-results">
            <h3 className="text-sm font-heading font-semibold mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" /> Frontend Tests
            </h3>
            <div className="cp-table-wrapper">
              <Table>
                <TableHeader><TableRow><TableHead className="w-8"></TableHead><TableHead>Test</TableHead><TableHead>Status</TableHead><TableHead>Message</TableHead><TableHead className="text-right">Duration</TableHead></TableRow></TableHeader>
                <TableBody>
                  {frontendTests.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell><Globe className="w-4 h-4 text-muted-foreground" /></TableCell>
                      <TableCell className="font-medium text-sm">{r.test}</TableCell>
                      <TableCell>
                        {r.status === 'pass'
                          ? <span className="cp-badge-green flex items-center gap-1 w-fit"><CheckCircle className="w-3 h-3" />Pass</span>
                          : <span className="cp-badge-red flex items-center gap-1 w-fit"><XCircle className="w-3 h-3" />Fail</span>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.message}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.duration_ms}ms</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Timestamp */}
          <p className="text-xs text-muted-foreground text-center" data-testid="test-timestamp">
            <Clock className="w-3 h-3 inline mr-1" />
            Test executed at: {new Date(results.timestamp).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
